
import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';

import balloon_vShader from 'raw-loader!../../shaders/balloon_vShader.vert';
import balloon_fShader from 'raw-loader!../../shaders/balloon_fShader.frag';

/**
 * One object to load
 */
class ObjectToLoad {
    constructor(asset) {
        this.loaded = false;
        this.percentLoaded = 0;
        this.assetRef = asset;
    }
}

/**
 * A Loader3D: loads mesh and textures based on a group id
 * Final loaded THREE Groups will be in loadedGroups array
 */
class Loader3D {
    constructor(loadingManager, fullLoader = false, product = false) {

        this.loadingManager = loadingManager // Loading manager instance: gets acces to loader objects and App global instance

        this.typeMaps = ['map', 'aoMap', 'roughnessMap', 'metalnessMap', 'normalMap', 'lightMap', 'clearcoatNormalMap', 'alphaMap']

        this.loadedGroups = [] //final THREE groups loaded here

        this.product = product

        this.id = null
        this.loaded = false,
        this.started = false,
        this.percentCompleted = 0,
        this.objectsLoaded= 0,
        this.objectsToLoad = {
            meshes: {},
            textures: {},
            materials: {},
            groups: {}
        },

        this.fullLoader = fullLoader // is this loading fullscreen (hides the scene ?). False: subtle background loading
    }

    /**  loads texture and store it in the loadedObject attribute of the asset object */
    async loadTexture(texToLoad) {

        let promise = new Promise((resolve, reject) => {
            
            this.loadingManager.loadersTHREE.textureLoader.load(texToLoad.assetRef.url, (texture) => {

                if(texToLoad.assetRef.srgb)
                    texture.encoding = THREE.sRGBEncoding;

                if(texToLoad.assetRef.anisotropyMax)
                    texture.anisotropy = this.loadingManager.App.renderer.capabilities.getMaxAnisotropy()

                resolve(texture)
            }, undefined, (err) => {
                console.error(err)
                reject(err)
            })

        });
        
        let result = await promise;

        texToLoad.assetRef.loadedObject = result;
        texToLoad.percentLoaded = 100;
        texToLoad.loaded = true;
        this.objectsLoaded++;

        //console.log('A texture has finished loading:', texToLoad.assetRef.url)

        this.update();
        
    }

    /**  loads mesh and store it in the loadedObject attribute of the asset object */
    async loadMesh(meshToLoad) {

        let promise = new Promise((resolve, reject) => {
            
            this.loadingManager.loadersTHREE.FBXLoader.load( meshToLoad.assetRef.url, (object) => { //onLoad
                var mesh = object.children[0];
                mesh.position.set( meshToLoad.assetRef.position.x, meshToLoad.assetRef.position.y, meshToLoad.assetRef.position.z )
                mesh.rotation.set( meshToLoad.assetRef.rotation.x, meshToLoad.assetRef.rotation.y, meshToLoad.assetRef.rotation.z )
                mesh.scale.set( meshToLoad.assetRef.scale.x, meshToLoad.assetRef.scale.y, meshToLoad.assetRef.scale.z )
                resolve(mesh)
            },
            (xhr) => { // onProgress
                meshToLoad.percentLoaded = (xhr.loaded / xhr.total * 100);
            },
            (err) => {
                console.error('error loading mesh', err)
                reject(err)
            } )
        })
        
        let result = await promise;

        meshToLoad.assetRef.loadedObject = result;
        meshToLoad.percentLoaded = 100;
        meshToLoad.loaded = true;
        this.objectsLoaded++;

       // console.log('A mesh has finished loading:', meshToLoad.assetRef.url)

        this.update();
        
    }

    loadTextureEvent(texId) {
        this.loadingManager.loadersTHREE.textureLoader.load(this.objectsToLoad.textures[texId].assetRef.url, (texture) => {

            if(this.objectsToLoad.textures[texId].assetRef.srgb)
                texture.encoding = THREE.sRGBEncoding;

            if(this.objectsToLoad.textures[texId].assetRef.anisotropyMax)
                texture.anisotropy = this.loadingManager.App.renderer.capabilities.getMaxAnisotropy()

            this.objectsToLoad.textures[texId].assetRef.loadedObject = texture;
            this.objectsToLoad.textures[texId].percentLoaded = 100;
            this.objectsToLoad.textures[texId].loaded = true;
            this.objectsLoaded++;
    
            //console.log('A texture has finished loading:', texToLoad.assetRef.url)
    
            this.update();

            
        }, undefined, (err) => {
            console.error(err)
            
        })
    }

    loadMeshEvent(meshId) {
        this.loadingManager.loadersTHREE.FBXLoader.load( this.objectsToLoad.meshes[meshId].assetRef.url, (object) => { //onLoad

            var mesh = object.children[0];
            mesh.position.set( 
                this.objectsToLoad.meshes[meshId].assetRef.position.x, 
                this.objectsToLoad.meshes[meshId].assetRef.position.y, 
                this.objectsToLoad.meshes[meshId].assetRef.position.z 
            )
            mesh.rotation.set( 
                this.objectsToLoad.meshes[meshId].assetRef.rotation.x, 
                this.objectsToLoad.meshes[meshId].assetRef.rotation.y, 
                this.objectsToLoad.meshes[meshId].assetRef.rotation.z 
            )
            mesh.scale.set( 
                this.objectsToLoad.meshes[meshId].assetRef.scale.x, 
                this.objectsToLoad.meshes[meshId].assetRef.scale.y, 
                this.objectsToLoad.meshes[meshId].assetRef.scale.z
            )
            
            
            this.objectsToLoad.meshes[meshId].assetRef.loadedObject = mesh;
            this.objectsToLoad.meshes[meshId].percentLoaded = 100;
            this.objectsToLoad.meshes[meshId].loaded = true;
            this.objectsLoaded++;

            this.update();

        },
        (xhr) => { // onProgress
            this.objectsToLoad.meshes[meshId].percentLoaded = (xhr.loaded / xhr.total * 100);
        },
        (err) => {
            console.error('error loading mesh', err)
        } )
    }

    /** Starts loading meshes and textures
     * stores loaded objects them in every objectsToLoad assetRef.loadedObject 
     * Fires Loader3DFinished event when done */
    start() {

        this.started = true

        if(this.fullLoader)
            this.loadingManager.fullLoad.display = true
        else 
            this.loadingManager.subtleLoad.display = true

        for(var texId in this.objectsToLoad.textures) {

            if(Object.prototype.hasOwnProperty.call(this.objectsToLoad.textures, texId)) {
                //var texToLoad = this.objectsToLoad.textures[texId]
                //this.loadTexture(texToLoad)
                this.loadTextureEvent(texId)
            }
        }

        for(var meshId in this.objectsToLoad.meshes) {
            if(Object.prototype.hasOwnProperty.call(this.objectsToLoad.meshes, meshId)) {
                //var meshToLoad = this.objectsToLoad.meshes[meshId]
                this.loadMeshEvent(meshId)
            }

        }
        
    }

    /** Check current loader percentage, then fire a inished event if finished.
     * Called every time an object has finished loading
     */
    update() {

        var totalToLoad = Object.keys(this.objectsToLoad.textures).length + Object.keys(this.objectsToLoad.meshes).length;

        var percentLoaded = (this.objectsLoaded * 100) / totalToLoad;
        this.percentLoaded = percentLoaded;

        if(this.fullLoader) {
            this.loadingManager.fullLoad.percentCompleted = this.percentLoaded
        }
        else {
            this.loadingManager.subtleLoad.percentCompleted = this.percentLoaded
        }

        // If every objects has been loaded
        if(totalToLoad == this.objectsLoaded) {

            this.parseObjectsLoaded()
            this.loaded = true
            document.dispatchEvent( new CustomEvent( 'loader3DFinished', { 'detail' : {
                loader3D: this
            }}))

        }
            

    }

    /** Will create THREE group, mesh, material and texture. Final groups will be on loadedGroups array */
    parseObjectsLoaded() {

        // for each groupId loaded
        for(var groupId in this.objectsToLoad.groups) {
            if(Object.prototype.hasOwnProperty.call(this.objectsToLoad.groups, groupId)) { // direct property failsafe

                var assetGroup = this.objectsToLoad.groups[groupId].assetRef // get asset object for this group       

                //Creates the THREE group object
                var group = new THREE.Group();
                group.name = assetGroup.name;

                // respects asset group transform for the final THREE Group
                group.position.set(assetGroup.position.x, assetGroup.position.y, assetGroup.position.z)
                group.rotation.set(assetGroup.rotation.x, assetGroup.rotation.y, assetGroup.rotation.z)
                group.scale.set(assetGroup.scale.x, assetGroup.scale.y, assetGroup.scale.z)

                assetGroup.meshes.forEach( (assetGroupMesh) => { // every asset mesh ref for this group

                    var assetMesh = this.getAssetMeshFromId(assetGroupMesh.meshid)
                    var mesh = assetMesh.loadedObject; // Gets the THREE Mesh object loaded

                    // respecting mesh position and rotation on group mesh list
                    mesh.position.set(assetGroupMesh.position.x, assetGroupMesh.position.y, assetGroupMesh.position.z)
                    mesh.rotation.set(assetGroupMesh.rotation.x, assetGroupMesh.rotation.y, assetGroupMesh.rotation.z)
                    mesh.scale.set(assetGroupMesh.scale.x, assetGroupMesh.scale.y, assetGroupMesh.scale.z)

                    // on THREE, material is an array of material if more than one
                    if(assetMesh.materials.length > 1)
                        mesh.material = []
                    
                    var matIndex = 0;
                    assetMesh.materials.forEach ( (materialAssetId) => { // every material asset ID associated with this mesh

                        var assetMaterial = this.getAssetMaterialFromId(materialAssetId)

                        assetMaterial.realAttr = Object.assign({}, assetMaterial.attr) // copy mat attribute with texture ref id for replacecing with real loaded textures

                        // searching for revery loaded texture on this material, than putting them in realAttr object
                        this.typeMaps.forEach( (type) => {
                            if(assetMaterial.attr[type] != null) {
                                //console.log(assetMaterial.attr[type])

                                var assetTexture = this.getAssetTextureFromId(assetMaterial.attr[type]) // gets asset texture object from id
                                var texture = assetTexture.loadedObject // getting the loaded texture

                                assetMaterial.realAttr[type] = texture //putting the texture into the corresponding attribute
                                texture.needsUpdate = true

                                //console.log('parseObjectLoaded texture', assetTexture)
                            }
                        })

                        var material = this.makeMaterialFromLoadedAssetMaterial(assetMaterial)
                        material.needsUpdate = true

                        // on THREE, material is an array of material if more than one
                        if(assetMesh.materials.length > 1)
                            mesh.material[matIndex] = material
                        else
                            mesh.material = material

                        matIndex++
                    })

                    group.add(mesh)

                })

                // THREE Group pushed in the global loadedGroups array
                this.loadedGroups.push(group)

            }
        }

    }

    /** Creathe the THREE material from  asset material attribute
     * (loaded texture objects are in realAttr attribute)
     */
    makeMaterialFromLoadedAssetMaterial(assetMaterial) {

        var matAttr = {}

        for(var attrName in assetMaterial.realAttr) {
            if(Object.prototype.hasOwnProperty.call(assetMaterial.realAttr, attrName)) {

                var attrValue = assetMaterial.realAttr[attrName]

                if(attrValue != null) {
                    matAttr[attrName] = attrValue
                }

            }
        }

        var finalMat = new THREE.MeshBasicMaterial({ color: 0x2c2c2c})

        var ballon_uniforms = {
            tCube: { value: this.loadingManager.App.hdrCubeMap}
        }

        var ballon_mat_0 = new THREE.ShaderMaterial({
            uniforms: ballon_uniforms,
            vertexShader: balloon_vShader,
            fragmentShader: balloon_fShader,
            blending: THREE.NormalBlending,
            depthTest: true,
            transparent: true,
            side: THREE.FrontSide
        });

        switch(assetMaterial.type) {
            case 0:
                finalMat = new THREE.MeshBasicMaterial( matAttr )
                break;
            case 1:
                finalMat = new THREE.MeshStandardMaterial( matAttr )
                break;
            case 2:
                finalMat = new THREE.MeshPhysicalMaterial( matAttr )
                break;
            case 3:
                finalMat = new THREE.MeshLambertMaterial( matAttr )
                break;
            case 4:
                finalMat = ballon_mat_0
                break;
        }

        return finalMat
    }

    /** return Group asset from id */
    getAssetGroupFromId(idGroup) {      
        return this.loadingManager.App.assets.groups.find( (group) => group.id == idGroup );
    }
    /** return Mesh asset from id */
    getAssetMeshFromId(idMesh) {      
        return this.loadingManager.App.assets.meshes.find( (mesh) => mesh.id == idMesh );
    }
    /** Returns Material asset from id */
    getAssetMaterialFromId(idMaterial) {
        return this.loadingManager.App.assets.materials.find( (mat) => mat.id == idMaterial );
    }
    /** Returns Material asset from id */
    getAssetTextureFromId(idTexture) {
        return this.loadingManager.App.assets.textures.find( (tex) => tex.id == idTexture );
    }

    /** adds every dependancy asset to load for "group" in "objectsToLoad" */
    prepareLoadAssetGroup(group) {
        this.objectsToLoad.groups[group.id] = new ObjectToLoad(group)
        
        group.meshes.forEach( (grpMesh) => {
            this.prepareLoadAssetMesh( this.getAssetMeshFromId(grpMesh.meshid), this.objectsToLoad);
        });
    }

    /** adds every dependancy asset to load for "mesh" in "objectsToLoad" */
    prepareLoadAssetMesh(mesh) {
        this.objectsToLoad.meshes[mesh.id] = new ObjectToLoad(mesh)

        mesh.materials.forEach( (materialId) => {
            this.prepareLoadAssetMaterial( this.getAssetMaterialFromId(materialId), this.objectsToLoad);
        });
    }

    /** adds every dependancy asset to load for "material" in "objectsToLoadArray" */
    prepareLoadAssetMaterial(material) {

        this.objectsToLoad.materials[material.id] = material

        this.typeMaps.forEach( (type) => {

            if(material.attr[type] != null) {
                this.objectsToLoad.textures[material.attr[type]] = new ObjectToLoad( this.getAssetTextureFromId(material.attr[type]) ) 
            }

        })
    }

}

/**
 * Handles loading
 */
export default class LoadingManagerGlobal {
    constructor(app) {

        this.App = app;
        this.threeLoadingManager = new THREE.LoadingManager()

        this.loadersTHREE = {
            FBXLoader: new FBXLoader(this.threeLoadingManager),
            textureLoader: new THREE.TextureLoader(this.threeLoadingManager)
        }

        this.events = {
            baseLoaded : new Event('baseLoaded'), // base scene and objects have been loaded : App can display (first load at launch)
        }

        this.fullLoad = {
            display: false,
            percentCompleted : 0
        }

        this.subtleLoad = {
            display: false,
            percentCompleted : 0
        }

        this.loaders3D = []


        document.addEventListener('loader3DFinished', (e) => { this.loader3DFinished(e); })

    }

    
    /** A loader has done his work
     * every loaded object shoud be his objectsToLoad object*/
    loader3DFinished() {

        
        // disable full load if no loader is active and full
        if(this.loaders3D.filter( l => !l.loaded && l.started && l.fullLoader).length == 0)
            this.fullLoad.display = false
        
        if(this.loaders3D.filter( l => !l.loaded && l.started && !l.fullLoader).length == 0)
            this.subtleLoad.display = false
        

    }

    /*  =======================================
                LOADING SCENE
        ======================================= 
    */

    loadSceneMeshes() {

        if(this.App.debug)
            console.time('loadSceneMeshes')

        // creates a new loader3D instance
        var loader3D = this.createLoader3D(true, false)
        
        // prepare objects to load 
        this.App.room.groupId.forEach( (groupId) => {
            loader3D.prepareLoadAssetGroup( loader3D.getAssetGroupFromId(groupId));
        })

        // start the loader
        loader3D.start()

        if(this.App.debug)
            console.timeEnd('loadSceneMeshes')

    }

    /** Creates a new loader3D instance, adds it the the loader list
     * @param {*} fullLoader is the loader fullscreen ? True: hides the scene and displays big progress bar. False: subtle background loader
     * @param {Boolean} product is it for loading a product / system ?
     */
    createLoader3D(fullLoader = false, product = false) {
        var loader3D = new Loader3D(this, fullLoader, product)
        this.loaders3D.push(loader3D)
        loader3D.id = this.loaders3D.length - 1
        return loader3D
    }

    

    /**
     * 
     * @param {String} path url/path of file
     * @param {Boolean} json JSON parse before return ?
     * @param {Function} success success callback
     * @param {Function} error erro callback
     */
    loadFile(path, json, success, error)
    {
        var xhr = new XMLHttpRequest();
        xhr.onreadystatechange = function()
        {
            if (xhr.readyState === XMLHttpRequest.DONE) {
                if (xhr.status === 200) {
                    if (success) {
                        if(json)
                            success(JSON.parse(xhr.responseText));
                        else
                            success(xhr.responseText);
                    }
                        
                } else {
                    if (error)
                        error(xhr);
                }
            }
        };
        xhr.open("GET", path, true);
        xhr.send();
    }

    

}